// src/api/features/currencies/api.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  Currency,
  CurrencyListQuery,
  PaginatedCurrencyItems,
  PublicCurrency,
  CreateCurrencyPayload,
  UpdateCurrencyPayload,
} from "./types";

/* ======================================================
 * Helpers
 * ====================================================== */

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }

  const s = u.toString();
  return s ? `?${s}` : "";
}

/* ======================================================
 * Base URL
 * ------------------------------------------------------
 * Expected backend route:
 *   /api/v1/currencies
 * so this should usually be PATHS.currencies.base
 * ====================================================== */

const CURRENCIES_BASE = PATHS.currencies.base;

/* ======================================================
 * LIST – GET /currencies
 * Returns unwrapped pagination for FE: PaginatedCurrencyItems
 * ====================================================== */

export async function listCurrencies(
  query: CurrencyListQuery = {}
): Promise<PaginatedCurrencyItems> {
  const safePage = Math.max(1, Number(query.page ?? 1));

  // FE uses `limit`, backend expects `pageSize`
  const rawPageSize = Number(query.limit ?? 20);
  const safePageSize = Math.max(
    1,
    Math.min(200, Number.isFinite(rawPageSize) ? rawPageSize : 20)
  );

  const url = `${CURRENCIES_BASE}${qs({
    page: safePage,
    pageSize: safePageSize,
    q: query.q,
    includeDeleted: query.includeDeleted,
  })}`;

  const res = await request<any>({
    method: "GET",
    url,
  });

  // 1) Backend might already return the paginated object directly:
  //    { items, page, pageSize, total }
  if (
    res &&
    typeof res === "object" &&
    "items" in res &&
    "page" in res &&
    "pageSize" in res &&
    "total" in res
  ) {
    const d = res as PaginatedCurrencyItems;

    return {
      items: d.items ?? [],
      page: Number(d.page ?? safePage),
      pageSize: Number(d.pageSize ?? safePageSize),
      total: Number(d.total ?? d.items?.length ?? 0),
    };
  }

  // 2) Backend returns an envelope:
  //    { ok, data: { items, page, pageSize, total } }
  // or { ok, data: Currency[], meta }
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const env = res as ApiEnvelope<unknown> & { meta?: any };
    const data = env.data;

    // 2a) data is the paginated object
    if (data && typeof data === "object" && "items" in (data as any)) {
      const d = data as PaginatedCurrencyItems;

      return {
        items: d.items ?? [],
        page: Number(d.page ?? safePage),
        pageSize: Number(d.pageSize ?? safePageSize),
        total: Number(d.total ?? d.items?.length ?? 0),
      };
    }

    // 2b) data is plain array + meta
    if (Array.isArray(data)) {
      const items = data as Currency[];
      const meta = env.meta ?? {};

      return {
        items,
        page: Number(meta.page ?? safePage),
        pageSize: Number(meta.pageSize ?? meta.limit ?? safePageSize),
        total: Number(meta.total ?? items.length ?? 0),
      };
    }
  }

  // 3) Fallback – assume it's already PaginatedCurrencyItems
  return res as PaginatedCurrencyItems;
}

/* ======================================================
 * DETAIL – GET /currencies/:id
 * Returns: Currency (backend may send extra audit fields)
 * ====================================================== */

export async function getCurrency(id: number | string): Promise<Currency> {
  const res = await request<Currency | ApiEnvelope<Currency>>({
    method: "GET",
    url: `${CURRENCIES_BASE}/${id}`,
  });

  return unwrap<Currency>(res);
}

/* ======================================================
 * CREATE – POST /currencies
 * Body: CreateCurrencyPayload
 * Returns: Currency
 * ====================================================== */

export async function createCurrency(
  payload: CreateCurrencyPayload
): Promise<Currency> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[createCurrency] payload →", dto);
  }

  const res = await request<Currency | ApiEnvelope<Currency>>({
    method: "POST",
    url: CURRENCIES_BASE,
    data: dto,
  });

  return unwrap<Currency>(res);
}

/* ======================================================
 * UPDATE – PUT /currencies/:id
 * Body: UpdateCurrencyPayload
 * Returns: Currency
 * ====================================================== */

export async function updateCurrency(
  id: number | string,
  payload: UpdateCurrencyPayload
): Promise<Currency> {
  const dto: any = { ...payload };

  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[updateCurrency] id=", id, "payload →", dto);
  }

  const res = await request<Currency | ApiEnvelope<Currency>>({
    method: "PUT",
    url: `${CURRENCIES_BASE}/${id}`,
    data: dto,
  });

  return unwrap<Currency>(res);
}

/* ======================================================
 * DELETE – DELETE /currencies/:id
 * Returns: simple message or nothing
 * ====================================================== */

export async function deleteCurrency(
  id: number | string
): Promise<{ message: string } | void> {
  const res = await request<
    { message: string } | ApiEnvelope<{ message: string }>
  >({
    method: "DELETE",
    url: `${CURRENCIES_BASE}/${id}`,
  });

  // If API returns plain 204 No Content, res may be undefined
  if (!res) return;

  return unwrap<{ message: string }>(res);
}

export async function listPublicCurrencies(params: {
  q?: string;
  limit?: number;
} = {}): Promise<PublicCurrency[]> {
  const url = `${PATHS.currencies.public}${qs({
    q: params.q,
    limit: params.limit,
  })}`;

  const res = await request<any>({
    method: "GET",
    url,
  });

  if (Array.isArray(res)) {
    return res as PublicCurrency[];
  }

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const data = (res as ApiEnvelope<unknown>).data;
    return Array.isArray(data) ? (data as PublicCurrency[]) : [];
  }

  return [];
}
